import { useCallback, useEffect, useRef, useState } from 'react'

/** Keep this much of the card on screen so a drag can never strand it. */
const KEEP_VISIBLE = 48

/** Elements that own the pointer themselves — a drag must never start on them. */
const INTERACTIVE = 'button, a, input, textarea, select, [role="radio"], [data-no-drag]'

export interface DragOffset {
  readonly x: number
  readonly y: number
}

/**
 * Lets the learner drag a coach-mark card out of its computed slot.
 *
 * The tour positions each card against the element it describes, but the two
 * can still collide — a tall bottom sheet, a dialog that opens where the card
 * already sits, a long body on a short viewport. Rather than hand-tuning
 * `placement` per step, this gives the learner the final say: press anywhere on
 * the card that is not itself interactive and drag it clear.
 *
 * The offset is deliberately reset whenever `resetKey` changes (the step id),
 * so each step starts from its designed position. A drag is an escape hatch for
 * the step you are on, not a preference that follows you through the lesson.
 */
export function useDraggableCard(resetKey: string) {
  const [offset, setOffset] = useState<DragOffset>({ x: 0, y: 0 })
  const [dragging, setDragging] = useState(false)
  const origin = useRef<{ px: number; py: number; ox: number; oy: number } | null>(null)
  const nodeRef = useRef<HTMLDivElement | null>(null)

  // biome-ignore lint/correctness/useExhaustiveDependencies: resetKey is the intentional trigger, not used in the effect body
  useEffect(() => {
    setOffset({ x: 0, y: 0 })
  }, [resetKey])

  const onPointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      // Let buttons, the language toggle and any text field keep their taps.
      if ((e.target as HTMLElement).closest(INTERACTIVE)) return
      // A scrollable body still needs to scroll; only drag when it cannot.
      const scroller = (e.target as HTMLElement).closest<HTMLElement>('[data-coachmark-scroll]')
      if (scroller && scroller.scrollHeight > scroller.clientHeight) return
      origin.current = { px: e.clientX, py: e.clientY, ox: offset.x, oy: offset.y }
      setDragging(true)
      e.currentTarget.setPointerCapture(e.pointerId)
    },
    [offset.x, offset.y],
  )

  const onPointerMove = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    const start = origin.current
    const node = nodeRef.current
    if (!start || !node) return
    const next = { x: start.ox + (e.clientX - start.px), y: start.oy + (e.clientY - start.py) }
    // Clamp against where the card would land, measured from its undragged box.
    const r = node.getBoundingClientRect()
    const left = r.left - start.ox + next.x
    const top = r.top - start.oy + next.y
    const minX = next.x - (left + r.width - KEEP_VISIBLE)
    const maxX = next.x + (window.innerWidth - KEEP_VISIBLE - left)
    const minY = next.y - (top + r.height - KEEP_VISIBLE)
    const maxY = next.y + (window.innerHeight - KEEP_VISIBLE - top)
    setOffset({
      x: Math.min(Math.max(next.x, minX), maxX),
      y: Math.min(Math.max(next.y, minY), maxY),
    })
  }, [])

  const onPointerUp = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    origin.current = null
    setDragging(false)
    if (e.currentTarget.hasPointerCapture(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId)
    }
  }, [])

  return {
    offset,
    dragging,
    nodeRef,
    dragHandlers: { onPointerDown, onPointerMove, onPointerUp, onPointerCancel: onPointerUp },
  }
}
